/**
 * Payments — every subscription event from the App Store, Google Play
 * (via RevenueCat) and web (Razorpay) in the last 12 months, with
 * revenue totals per store. Test (sandbox) purchases are flagged and
 * left out of the totals.
 *
 * Data: `adminListBillingEvents`.
 */
import {
  Anchor,
  Badge,
  Button,
  Card,
  Group,
  Select,
  SimpleGrid,
  Switch,
  Table,
  Text,
  TextInput,
} from '@mantine/core';
import {
  IconBrandAppstore,
  IconBrandGooglePlay,
  IconDownload,
  IconSearch,
  IconWorld,
} from '@tabler/icons-react';
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import {
  EmptyState,
  ErrorBlock,
  LoadingBlock,
  PageHeader,
  RefreshButton,
  StatCard,
  useAdminData,
} from '@/components/ui';
import type { BillingEvent } from '@/lib/adminTypes';
import { callAdmin } from '@/lib/firebase';
import {
  EVENT_LABEL,
  STORE_LABEL,
  TIER_LABEL,
  downloadCsv,
  fmtDateTime,
  fmtInr,
  fmtMoney,
  fmtRelative,
  label,
} from '@/lib/format';

const PAID = new Set(['INITIAL_PURCHASE', 'RENEWAL', 'PRODUCT_CHANGE', 'WEB_PAYMENT']);

const EVENT_FILTERS = [
  { value: 'paid', label: 'Payments only' },
  { value: 'INITIAL_PURCHASE', label: 'New subscriptions' },
  { value: 'RENEWAL', label: 'Renewals' },
  { value: 'CANCELLATION', label: 'Auto-renew turned off' },
  { value: 'EXPIRATION', label: 'Expired' },
  { value: 'BILLING_ISSUE', label: 'Payment failed' },
  { value: 'warning', label: 'Not matched to a plan' },
];

function isRevenue(e: BillingEvent): boolean {
  return PAID.has(e.type) && e.environment !== 'SANDBOX' && !e.refunded && e.amount !== null;
}

export function Billing() {
  const { data, error, loading, reload } = useAdminData(() => callAdmin.listBillingEvents({}));
  const [search, setSearch] = useState('');
  const [store, setStore] = useState<string | null>(null);
  const [type, setType] = useState<string | null>(null);
  const [hideTests, setHideTests] = useState(true);

  const all = data?.rows ?? [];
  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return all.filter((e) => {
      if (hideTests && e.environment === 'SANDBOX') return false;
      if (store && e.store !== store) return false;
      if (type === 'paid' && !PAID.has(e.type)) return false;
      if (type === 'warning' && !e.warning) return false;
      if (type && type !== 'paid' && type !== 'warning' && e.type !== type) return false;
      if (!q) return true;
      return [e.orgName, e.orgId, e.productId].filter(Boolean).some((v) => (v as string).toLowerCase().includes(q));
    });
  }, [all, search, store, type, hideTests]);

  const totals = useMemo(() => {
    const now = Date.now();
    const out: Record<string, { amount: number; count: number; last30: number }> = {};
    for (const e of all) {
      if (!isRevenue(e) || e.currency !== 'INR' || !e.store) continue;
      const t = (out[e.store] ??= { amount: 0, count: 0, last30: 0 });
      t.amount += e.amount ?? 0;
      t.count += 1;
      if ((e.at ?? 0) >= now - 30 * 86_400_000) t.last30 += e.amount ?? 0;
    }
    return out;
  }, [all]);

  const exportCsv = () =>
    downloadCsv(
      `interiorio-payments-${new Date().toISOString().slice(0, 10)}.csv`,
      ['Date', 'Studio', 'Studio ID', 'Event', 'Plan', 'Period', 'Store', 'Test', 'Amount', 'Currency', 'Refunded', 'Note'],
      rows.map((e) => [
        fmtDateTime(e.at), e.orgName, e.orgId, label(EVENT_LABEL, e.type), label(TIER_LABEL, e.tier), e.period,
        label(STORE_LABEL, e.store), e.environment === 'SANDBOX' ? 'yes' : 'no', e.amount, e.currency,
        e.refunded ? 'yes' : 'no', e.warning,
      ]),
    );

  if (error && !data) return <ErrorBlock message={error} onRetry={reload} />;

  const tile = (key: 'app_store' | 'play_store' | 'razorpay', icon: JSX.Element, color: string) => {
    const t = totals[key];
    return (
      <StatCard
        label={`${STORE_LABEL[key]} · 12 months`}
        value={fmtInr(t?.amount ?? 0)}
        hint={t ? `${t.count} payment(s) · ${fmtInr(t.last30)} in the last 30 days` : 'No payments yet'}
        icon={icon}
        color={color}
      />
    );
  };

  return (
    <>
      <PageHeader
        title="Payments"
        subtitle="App Store, Google Play and web subscription events — last 12 months"
        actions={
          <>
            <Button variant="default" size="xs" leftSection={<IconDownload size={14} />} onClick={exportCsv} disabled={!rows.length}>
              Export CSV
            </Button>
            <RefreshButton onClick={reload} loading={loading} />
          </>
        }
      />

      {!data ? (
        <LoadingBlock />
      ) : (
        <>
          <SimpleGrid cols={{ base: 1, sm: 3 }} spacing="md" mb="md">
            {tile('app_store', <IconBrandAppstore size={22} />, 'dark')}
            {tile('play_store', <IconBrandGooglePlay size={22} />, 'green')}
            {tile('razorpay', <IconWorld size={22} />, 'blue')}
          </SimpleGrid>
          <Text size="xs" c="dimmed" mb="md">
            Amounts are what the customer paid (before Apple / Google fees). Store prices were recorded from
            29 Sep 2026 — earlier store events show the event without an amount.
          </Text>

          <Card withBorder radius="lg" p="md" mb="md">
            <Group gap="sm" wrap="wrap">
              <TextInput
                placeholder="Search studio or product…"
                leftSection={<IconSearch size={15} />}
                value={search}
                onChange={(e) => setSearch(e.currentTarget.value)}
                style={{ flex: '1 1 240px' }}
              />
              <Select
                placeholder="Any store"
                data={[
                  { value: 'app_store', label: 'App Store' },
                  { value: 'play_store', label: 'Google Play' },
                  { value: 'razorpay', label: 'Web (Razorpay)' },
                ]}
                value={store}
                onChange={setStore}
                clearable
                w={170}
              />
              <Select placeholder="Any event" data={EVENT_FILTERS} value={type} onChange={setType} clearable w={200} />
              <Switch label="Hide test purchases" checked={hideTests} onChange={(e) => setHideTests(e.currentTarget.checked)} />
            </Group>
          </Card>

          {rows.length === 0 ? (
            <Card withBorder radius="lg">
              <EmptyState title="No events match these filters" />
            </Card>
          ) : (
            <Card withBorder radius="lg" p={0}>
              <Table.ScrollContainer minWidth={900}>
                <Table highlightOnHover verticalSpacing="sm">
                  <Table.Thead bg="#F8FAFC">
                    <Table.Tr>
                      <Table.Th>Date</Table.Th>
                      <Table.Th>Studio</Table.Th>
                      <Table.Th>Event</Table.Th>
                      <Table.Th>Plan</Table.Th>
                      <Table.Th>Store</Table.Th>
                      <Table.Th style={{ textAlign: 'right' }}>Amount</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {rows.map((e) => (
                      <Table.Tr key={e.id}>
                        <Table.Td>
                          <Text size="sm">{fmtDateTime(e.at)}</Text>
                          <Text size="xs" c="dimmed">
                            {fmtRelative(e.at)}
                          </Text>
                        </Table.Td>
                        <Table.Td>
                          {e.orgId ? (
                            <Anchor component={Link} to={`/studios/${e.orgId}`} size="sm" fw={600}>
                              {e.orgName ?? e.orgId}
                            </Anchor>
                          ) : (
                            '—'
                          )}
                        </Table.Td>
                        <Table.Td>
                          <Text size="sm" fw={500} c={e.warning ? 'red' : undefined}>
                            {label(EVENT_LABEL, e.type)}
                          </Text>
                          {e.warning ? (
                            <Text size="xs" c="red">
                              {e.warning}
                            </Text>
                          ) : null}
                        </Table.Td>
                        <Table.Td>
                          <Text size="sm">
                            {e.tier ? `${label(TIER_LABEL, e.tier)}${e.period ? ` · ${e.period}` : ''}` : '—'}
                          </Text>
                        </Table.Td>
                        <Table.Td>
                          <Group gap={4}>
                            <Badge size="sm" variant="light" color={e.store === 'play_store' ? 'green' : e.store === 'razorpay' ? 'blue' : 'dark'}>
                              {label(STORE_LABEL, e.store)}
                            </Badge>
                            {e.environment === 'SANDBOX' ? (
                              <Badge size="sm" variant="light" color="yellow">
                                Test
                              </Badge>
                            ) : null}
                          </Group>
                        </Table.Td>
                        <Table.Td style={{ textAlign: 'right' }}>
                          <Text size="sm" fw={600} td={e.refunded ? 'line-through' : undefined}>
                            {fmtMoney(e.amount, e.currency)}
                          </Text>
                          {e.refunded ? (
                            <Text size="xs" c="dimmed">
                              Refunded
                            </Text>
                          ) : null}
                        </Table.Td>
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>
              </Table.ScrollContainer>
            </Card>
          )}
        </>
      )}
    </>
  );
}
